import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { DatePicker, TreeSelect, Switch } from "antd";
import { useSelector } from "react-redux";
import { Menu, MenuItem, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Avatar, Typography, Grid, Box, Divider } from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import moment from "moment";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { getDefaultForm } from "../../../core/common/formSelection";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import api from "../../../services/api";
import AddLeads from "./addLeads";
import EditLead from "./editLead";
import LeadsGridFilters from "./components/LeadsGridFilters";
import { SlidersHorizontal } from "lucide-react";
import CommonSelect from "../../../core/common/commonSelect";
import { showBasicAlert } from "../../../core/common/useAlert";
import { humanReadableDate } from "../../../helper/dateTimeHelper";
import "./LeadGrid.module.css";

import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../../core/data/redux/store";
import { callActions } from "../../../core/data/redux/live-call/slice";
import { acquireCallLock, getCallSocket, isCallLockedByOtherTab } from "../liveCall/services/callSocket";
import { notify } from "../../../services/toast.config";
import ReminderDropdown from "./components/ReminderDropdown";


interface FormField {
  id: string;
  label: string;
  type: "text" | "select";
  options?: string[];
}

interface FormData {
  structure: FormField[];
  responses?: { [key: string]: string };
  id?: any;
}

const LeadsGrid = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state: any) => state.user.user);
  const signature = localStorage.getItem("signature");
  const isRestoringFromPreserve = useRef(false);
  const routes = all_routes;

  const previousSelectedCompanyDetails = location.state?.companyDetails || null;
  const previousSelectedformDetails = location.state?.formDetails || null;

  const [isInitializing, setIsInitializing] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [getAllCompanyLists, setgetAllCompanyList] = useState<any[]>([]);
  const [getAllCompanyListsForAdd, setgetAllCompanyListForAdd] = useState<any[]>([]);
  const [data, setData] = useState<any[]>([]);
  const [transferLeadsId, setTransferLeadsId] = useState<any[]>([]);
  const [showOnlyTransferred, setShowOnlyTransferred] = useState(false);
  const [pipelines, setPipelines] = useState<any[]>([]);
  const [pipelinesColor, setPipelinesColor] = useState<any[]>([]);
  const [pipelinesConfig, setPipelinesConfig] = useState<any[]>([]);
  const [newTransferLead, setNewTransferLead] = useState<any[]>([]);
  const [leads, setLeads] = useState<any>({});
  const [HeaderGrid, setHeaderGrid] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [visibleCountByPipeline, setVisibleCountByPipeline] = useState<{ [key: string]: number }>({});

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string>("Recently Added");
  const [selectedLeadSource, setSelectedLeadSource] = useState<string | undefined>(undefined);
  const [selectedStartDate, setSelectedStartDate] = useState<moment.Moment>(moment().subtract(29, "days"));
  const [selectedEndDate, setSelectedEndDate] = useState<moment.Moment>(moment());

  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [ackLoading, setAckLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<FormData | null>(null);

  const [showReminderModal, setShowReminderModal] = useState(false);
  const [draggedLeadId, setDraggedLeadId] = useState<string | null>(null);
  const [selectedReminderDate, setSelectedReminderDate] = useState<any>(null);
  const [isDragProcessing, setIsDragProcessing] = useState(false);

  const [followUpFilter, setFollowUpFilter] = useState<string>("all");
  const [customFollowUpDate, setCustomFollowUpDate] = useState<moment.Moment | null>(null);
  const [overallStageCounts, setOverallStageCounts] = useState<{ [key: string]: number }>({});

  const [activityFilter, setActivityFilter] = useState<string>("all");
  const [activityStartDate, setActivityStartDate] = useState<moment.Moment>(moment().subtract(29, "days"));
  const [activityEndDate, setActivityEndDate] = useState<moment.Moment>(moment());
  const [activityLeadIds, setActivityLeadIds] = useState<number[] | null>(null);
  const [isActivityLoading, setIsActivityLoading] = useState(false);

  const [showFilters, setShowFilters] = useState(false);
  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const [reminderLeadIds, setReminderLeadIds] = useState<{ today: number[], overdue: number[], upcoming: number[] } | null>(null);
  const [reminderLeads, setReminderLeads] = useState<{ today: any[], overdue: any[], upcoming: any[] } | null>(null);
  const [isReminderLoading, setIsReminderLoading] = useState(false);
  const [reminderDropdownOpened, setReminderDropdownOpened] = useState(false);
  const [reminderCustomStart, setReminderCustomStart] = useState<moment.Moment | null>(null);
  const [reminderCustomEnd, setReminderCustomEnd] = useState<moment.Moment | null>(null);
  // ========== live call

  const dispatch = useDispatch<AppDispatch>();
  const call = useSelector((s: any) => s.liveCall);

  const handleCall = useCallback((lead: any) => {
    if (call.activeCall) return;

    if (isCallLockedByOtherTab()) {
      notify.error("A call is already active in another tab. Please end it first.");
      return;
    }

    acquireCallLock(user?.userDetails?.id);

    const callRecord = {
      ...lead,
      mobileNumber: lead.mobile_no || lead.mobileNumber || null,
      name: lead.response?.["Full Name"] || lead.name || "Unknown",
    };
    getCallSocket().emit("startCall", {
      userId: user?.userDetails?.id,
      data: callRecord,
    });
    dispatch(callActions.setActiveCall(callRecord));
    dispatch(callActions.setIsAutoCall(false));
    dispatch(callActions.setIsLeadsMode(true));
  }, [user, dispatch, call.activeCall]);

  //=====================================



  const followUpOptions = [
    { value: "today", label: "Today" },
    { value: "upcoming", label: "Upcoming" },
    { value: "overdue", label: "Overdue" },
  ];

  const activityFilterOptions = [
    { value: "all", label: "All Activity" },
    { value: "today", label: "Today's Activity" },
    { value: "yesterday", label: "Yesterday's Activity" },
    { value: "custom", label: "Custom Date" },
  ];

  //Refs for debouncing and preventing duplicate calls
  const prevFetchParamsRef = useRef<string>("");

  const [formDataCompanyChoose, setformDataCompanyChoose] = useState({
    companyName: "Select Company",
    companyId: "",
    companyType: "Leads",
  });

  const [formDataFormChoose, setformDataFormChoose] = useState({
    pipeline_id: "",
    form_name: "",
    form_id: "",
    user_id: "0",
    user_name: "All Employees",
  });

  const filterDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const leadSource = [
    {
      value: "Direct Calling", title: "Direct Calling", children: [
        { value: "Outbound Call (Manual)", title: "Outbound Call (Manual)" },
        { value: "Inbound Call", title: "Inbound Call" },
        { value: "Inbound via Campaign", title: "Inbound via Campaign" },
      ]
    },
    {
      value: "Messaging & Campaigns", title: "Messaging & Campaigns", children: [
        { value: "SMS Campaign", title: "SMS Campaign" },
        { value: "WhatsApp Campaign", title: "WhatsApp Campaign" },
      ]
    },
    {
      value: "Referrals", title: "Referrals", children: [
        { value: "Wholesaler/Distributor", title: "Wholesaler/Distributor" },
        { value: "Past Student Referral", title: "Past Student Referral" },
        { value: "Society Referral", title: "Society Referral" },
        { value: "Department Referral", title: "Department Referral" },
        { value: "Helpdesk", title: "Helpdesk" },
      ]
    },
    {
      value: "Social Media", title: "Social Media", children: [
        { value: "Facebook", title: "Facebook" },
        { value: "Instagram", title: "Instagram" },
        { value: "WhatsApp", title: "WhatsApp" },
      ]
    },
    { value: "Public Form", title: "Public Form" },
  ];

  const getLeadSourceFilter = useCallback(() => {
    if (!selectedLeadSource) return {};
    const parentValues = leadSource.map((item) => item.value);
    return parentValues.includes(selectedLeadSource)
      ? { parent_source: selectedLeadSource }
      : { child_source: selectedLeadSource };
  }, [selectedLeadSource]);

  const isPageLoading = useMemo(() => isInitializing, [isInitializing]);

  useEffect(() => {
    if (location.state?.preserveFilters) {
      isRestoringFromPreserve.current = true;
      const p = location.state.preserveFilters;

      setformDataCompanyChoose({
        companyId: p.companyId,
        companyName: p.companyName,
        companyType: p.companyType,
      });

      setformDataFormChoose({
        pipeline_id: p.pipeline_id,
        form_name: p.form_name,
        form_id: p.form_id,
        user_id: p.user_id,
        user_name: p.user_name,
      });

      setSelectedLeadSource(p.selectedLeadSource);
      setSelectedStartDate(moment(p.selectedStartDate));
      setSelectedEndDate(moment(p.selectedEndDate));
      setSortBy(p.sortBy || "Recently Added");
      setSearchQuery(p.searchQuery || "");
      setSearchInput(p.searchInput || "");
      setFollowUpFilter(p.followUpFilter || "all");
      setCustomFollowUpDate(p.customFollowUpDate ? moment(p.customFollowUpDate) : null);
      setShowOnlyTransferred(p.showOnlyTransferred || false);
      setActivityFilter(p.activityFilter || "all");
      setActivityStartDate(p.activityStartDate ? moment(p.activityStartDate) : moment().subtract(29, "days"));
      setActivityEndDate(p.activityEndDate ? moment(p.activityEndDate) : moment());
      setTempSearchQuery(p.tempSearchQuery || "");
      setShowFilters(p.showFilters || false);

      // Reset the flag after state updates have been triggered
      setTimeout(() => {
        isRestoringFromPreserve.current = false;
      }, 100);
    }
  }, [location.state]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (newTransferLead.length > 0) setIsTransferOpen(true);
  }, [newTransferLead]);

  useEffect(() => {
    const handler = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    if (getAllCompanyLists.length) {
      const defaultCompany = previousSelectedCompanyDetails || getAllCompanyLists[0];
      if (defaultCompany) {
        setformDataCompanyChoose({
          companyId: previousSelectedCompanyDetails?.companyId || defaultCompany.id,
          companyName: previousSelectedCompanyDetails?.companyName || defaultCompany.name,
          companyType: previousSelectedCompanyDetails?.companyType || defaultCompany.type,
        });
      }
    }
  }, [getAllCompanyLists]);

  useEffect(() => {
    if (formDataCompanyChoose.companyId) {
      fetchForms();
    }
  }, [formDataCompanyChoose.companyId, formDataCompanyChoose.companyType]);

  useEffect(() => {
    if (isRestoringFromPreserve.current) {
      return;
    }

    if (data.length && formDataCompanyChoose.companyId) {
      const defaultForm = previousSelectedformDetails || getDefaultForm(data);
      if (defaultForm) {
        setformDataFormChoose({
          pipeline_id: previousSelectedformDetails?.pipeline_id || defaultForm.pipeline_id,
          form_name: previousSelectedformDetails?.form_name || defaultForm.LeadFormName,
          form_id: previousSelectedformDetails?.form_id || defaultForm.key,
          user_id: previousSelectedformDetails?.user_id || "0",
          user_name: previousSelectedformDetails?.user_name || "All Employees",
        });
      }
    } else if (data.length === 0) {
      setformDataFormChoose({
        pipeline_id: "",
        form_name: "No Lead Form",
        form_id: "",
        user_id: "0",
        user_name: "All Employees",
      });
    }
  }, [data]);

  useEffect(() => {
    if (!formDataFormChoose.form_id) return;
    setReminderDropdownOpened(false);
    setReminderLeadIds(null);
    setReminderLeads(null);
  }, [formDataFormChoose.form_id]);

  useEffect(() => {
    if (!formDataFormChoose.form_id) return;
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);

    filterDebounceRef.current = setTimeout(() => {
      fetchLeads();
    }, 500);

    return () => {
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    };
  }, [formDataFormChoose.form_id, formDataFormChoose.user_id, selectedLeadSource, selectedStartDate, selectedEndDate]);

  const fetchCompanies = async () => {
    try {
      setIsInitializing(true);
      const response = await api.post("userService/getAllCompanyList", { signature });
      setgetAllCompanyList(response.data.data.data || []);
    } catch (error) {
      console.error("Error fetching companies:", error);
      showBasicAlert("Failed to load companies");
    } finally {
      setIsInitializing(false);
    }
  };

  const fetchForms = async () => {
    try {
      setIsLoadingData(true);
      const response = await api.post("leadService/getLeadFormsByCompany", {
        signature,
        company_id: formDataCompanyChoose.companyId,
        company_type: formDataCompanyChoose.companyType,
      });
      setData(response.data.data.data || []);
    } catch (error) {
      console.error("Error fetching forms:", error);
      showBasicAlert("Failed to load forms");
      setData([]);
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    const fetchActivityLeadIds = async () => {
      if (activityFilter === "all") {
        setActivityLeadIds(null);
        return;
      }

      if (activityFilter === "custom" && (!activityStartDate || !activityEndDate)) {
        return;
      }

      try {
        setIsActivityLoading(true);
        let startDateStr = "";
        let endDateStr = "";

        if (activityFilter === "today") {
          startDateStr = moment().startOf('day').format("YYYY-MM-DD");
          endDateStr = moment().add(1, "days").startOf('day').format("YYYY-MM-DD");
        } else if (activityFilter === "yesterday") {
          startDateStr = moment().subtract(1, "days").startOf('day').format("YYYY-MM-DD");
          endDateStr = moment().startOf('day').format("YYYY-MM-DD");
        } else if (activityFilter === "custom" && activityStartDate && activityEndDate) {
          startDateStr = activityStartDate.format("YYYY-MM-DD");
          endDateStr = moment(activityEndDate).add(1, "days").format("YYYY-MM-DD");
        }

        const response = await api.post(`leadService/getLeadIdsByActivity`, {
          signature,
          company_id: formDataCompanyChoose.companyId,
          company_type: formDataCompanyChoose.companyType,
          form_id: formDataFormChoose.form_id,
          activity_start_date: startDateStr,
          activity_end_date: endDateStr,
        });

        if (response.data?.msg === "Data Found" && response.data?.data?.lead_ids) {
          setActivityLeadIds(response.data.data.lead_ids);
        } else {
          setActivityLeadIds([]);
        }
      } catch (error) {
        console.error("Error fetching activity lead IDs:", error);
        setActivityLeadIds([]);
      } finally {
        setIsActivityLoading(false);
      }
    };

    if (formDataCompanyChoose.companyId && formDataFormChoose.form_id) {
      fetchActivityLeadIds();
    }
  }, [activityFilter, activityStartDate, activityEndDate, formDataCompanyChoose, formDataFormChoose.form_id, signature]);

  useEffect(() => {
    const fetchReminderLeadIds = async () => {
      if (!reminderDropdownOpened) return;
      if (!formDataFormChoose.form_id) return;

      try {
        setIsReminderLoading(true);
        const response = await api.post("leadService/getLeadReminder", {
          signature,
          company_id: formDataCompanyChoose.companyId,
          company_type: formDataCompanyChoose.companyType,
          form_id: formDataFormChoose.form_id,
          limit: 100000,
        });
        if (response.data?.message?.message === "Data Found" && response.data?.message?.reminder) {
          const { today, due, upcoming } = response.data.message.reminder;

          // Deduplicate each bucket by lead key — the API returns one row per reminder_id,
          // so the same lead can appear multiple times per bucket.
          const dedup = (arr: any[]) => Array.from(new Map(arr.map((l: any) => [Number(l.key), l])).values());
          const todayUniq = dedup(today || []);
          const overdueUniq = dedup(due || []);
          const upcomingUniq = dedup(upcoming || []);

          setReminderLeadIds({
            today: todayUniq.map((l: any) => Number(l.key)),
            overdue: overdueUniq.map((l: any) => Number(l.key)),
            upcoming: upcomingUniq.map((l: any) => Number(l.key)),
          });
          setReminderLeads({
            today: todayUniq,
            overdue: overdueUniq,
            upcoming: upcomingUniq,
          });
        } else {
          setReminderLeadIds(null);
          setReminderLeads(null);
        }
      } catch (error) {
        console.error("Error fetching reminder lead IDs:", error);
        setReminderLeadIds(null);
        setReminderLeads(null);
      } finally {
        setIsReminderLoading(false);
        setReminderDropdownOpened(false);
      }
    };

    fetchReminderLeadIds();
  }, [reminderDropdownOpened, formDataFormChoose.form_id, formDataCompanyChoose.companyId, signature]);

  const fetchLeads = async () => {
    if (!formDataFormChoose.form_id || !formDataFormChoose.pipeline_id) return;

    // Prevent duplicate API calls
    const fetchParams = JSON.stringify({
      companyId: formDataCompanyChoose.companyId,
      formId: formDataFormChoose.form_id,
      userId: formDataFormChoose.user_id,
      source: selectedLeadSource,
      start: selectedStartDate.format("YYYY-MM-DD"),
      end: selectedEndDate.format("YYYY-MM-DD"),
    });

    if (prevFetchParamsRef.current === fetchParams) return;
    prevFetchParamsRef.current = fetchParams;

    try {
      setIsLoadingData(true);

      const [pipelineRes, leadsRes, settingsRes, transferRes, stageCountsRes] = await Promise.all([
        api.post("leadService/getAllPipelinesById", { signature, id: formDataFormChoose.pipeline_id }),
        api.post("leadService/getAllLeadsResponse", {
          signature,
          company_id: formDataCompanyChoose.companyId,
          company_type: formDataCompanyChoose.companyType,
          start_date: selectedStartDate.format("YYYY-MM-DD"),
          end_date: selectedEndDate.format("YYYY-MM-DD"),
          form_id: formDataFormChoose.form_id,
          ...(formDataFormChoose.user_id !== "0" && { user_id: formDataFormChoose.user_id }),
          ...getLeadSourceFilter(),
          limit: 100000,
        }),
        api.post("leadService/getAllLeadSettings", {
          signature,
          company_id: parseInt(formDataCompanyChoose.companyId),
          company_type: formDataCompanyChoose.companyType,
        }),
        api.post("leadService/getTransferLeadsByUser", {
          signature,
          company_id: formDataCompanyChoose.companyId,
          company_type: formDataCompanyChoose.companyType,
          form_id: formDataFormChoose.form_id,
        }),
        api.post("leadService/getOverallStageCounts", {
          signature,
          company_id: formDataCompanyChoose.companyId,
          company_type: formDataCompanyChoose.companyType,
          form_id: formDataFormChoose.form_id,
          ...(formDataFormChoose.user_id !== "0" && { user_id: formDataFormChoose.user_id }),
          ...getLeadSourceFilter(),
        }),
      ]);

      const stages = pipelineRes.data.data.data[0]?.Stages || [];
      const colors = pipelineRes.data.data.data[0]?.Color || [];
      const stagesConfig = pipelineRes.data.data.data[0]?.Stages_Config || [];
      const fetchedLeads = leadsRes.data.data.data || [];
      const fetchedTransferLeadsId = leadsRes.data.data.transferLeadIds || [];
      const transfers = transferRes.data.data.data || [];
      const stageCounts = stageCountsRes.data.data.overallStageCounts || {};
      setOverallStageCounts(stageCounts);

      setTransferLeadsId(fetchedTransferLeadsId);


      setPipelines(stages);
      setPipelinesColor(colors);
      setPipelinesConfig(stagesConfig);
      setNewTransferLead(transfers);

      if (users.length === 0) {
        const userRes = await api.post("userService/getLeadEmployeesListData", {
          signature,
          department: settingsRes?.data?.data?.data[0]?.settings?.lead_assigned_to_department || -1,
          company_id: parseInt(formDataCompanyChoose.companyId),
          company_type: formDataCompanyChoose.companyType,
        });
        setUsers(userRes.data?.data?.data?.allUsers || []);
      }

      const groupedLeads = stages.reduce((acc: any, stage: any) => {
        acc[stage] = fetchedLeads.filter((lead: any) => lead.pipeline_char?.toLowerCase() === stage.toLowerCase());
        return acc;
      }, {});

      setLeads(groupedLeads);

      const items = fetchedLeads[0]?.Items || {
        "Total Leads": 0,
        "Today's Leads": 0,
        "Yesterday's Leads": 0,
        "This Month's Leads": 0,
      };

      const statsConfig: any = {
        "Total Leads": { icon: "ti-stack-middle", color: "bg-primary" },
        "Today's Leads": { icon: "ti-calendar", color: "bg-success" },
        "Yesterday's Leads": { icon: "ti-calendar-event", color: "bg-warning" },
        "This Month's Leads": { icon: "ti-calendar-month", color: "bg-info" },
      };

      const stats = Object.entries(items).map(([key, value]: any) => ({
        title: key,
        value,
        icon: statsConfig[key]?.icon || "ti-briefcase",
        color: statsConfig[key]?.color || "bg-secondary",
      }));

      setHeaderGrid(stats);

      const filteredCompanies = getAllCompanyLists.filter(
        (c) => c.id === Number(formDataCompanyChoose.companyId) && c.type === formDataCompanyChoose.companyType
      );
      setgetAllCompanyListForAdd(filteredCompanies);

      setVisibleCountByPipeline((prev) => {
        const next: any = {};
        stages.forEach((st: string) => { next[st] = prev[st] || 30; });
        return next;
      });

    } catch (error) {
      console.error("Error fetching leads:", error);
      showBasicAlert("Failed to load leads");
    } finally {
      setIsLoadingData(false);
    }
  };

  const forceRefreshLeads = useCallback(() => {
    prevFetchParamsRef.current = "";
    fetchLeads();
  }, [fetchLeads]);

  // Listen for custom refresh event after call outcome submission
  useEffect(() => {
    const handleRefreshAfterSubmission = () => {
      forceRefreshLeads();
    };

    window.addEventListener('refreshCallsAfterSubmission', handleRefreshAfterSubmission);

    return () => {
      window.removeEventListener('refreshCallsAfterSubmission', handleRefreshAfterSubmission);
    };
  }, [forceRefreshLeads]);

  const handleCompanyChange = (selectedOption: any) => {
    if (!selectedOption?.value) return;
    setformDataCompanyChoose({
      companyId: selectedOption.value,
      companyName: selectedOption.label.split("-")[0].trim(),
      companyType: selectedOption.label.split("-")[1].trim(),
    });
  };

  const handleLeadFormChange = (selectedOption: any) => {
    if (!selectedOption?.value) return;
    setformDataFormChoose((prev: any) => ({
      ...prev,
      pipeline_id: parseInt(selectedOption.value.split("-")[0]),
      form_name: selectedOption.label,
      form_id: parseInt(selectedOption.value.split("-")[1]),
    }));
  };

  const handleUserChange = (selectedOption: any) => {
    if (!selectedOption?.value) return;
    setformDataFormChoose((prev) => ({
      ...prev,
      user_id: selectedOption.value,
      user_name: selectedOption.label,
    }));
  };

  const handleDateChange = (start: moment.Moment, end: moment.Moment) => {
    setSelectedStartDate(start);
    setSelectedEndDate(end);
  };

  const handleLeadSourceChange = (value: string) => {
    setSelectedLeadSource(value);
  };

  const handleClearLeadSource = () => {
    isRestoringFromPreserve.current = false;
    setSelectedLeadSource(undefined);
    setSelectedStartDate(moment().subtract(29, "days"));
    setSelectedEndDate(moment());
    setFollowUpFilter("all");
    setCustomFollowUpDate(null);
    setShowOnlyTransferred(false);
    setActivityFilter("all");
    setActivityStartDate(moment().subtract(29, "days"));
    setActivityEndDate(moment());
    setActivityLeadIds(null);
    setSearchQuery("");
    setTempSearchQuery("");
    setSearchInput("");
    setSortBy("Recently Added");
    setformDataFormChoose((prev) => ({
      ...prev,
      user_id: "0",
      user_name: "All Employees",
    }));
  };

  const handleSearchApply = useCallback(() => {
    setSearchQuery(tempSearchQuery);
  }, [tempSearchQuery]);

  const activeFilterCount = useMemo(() => [
    formDataFormChoose.user_id !== "0" && formDataFormChoose.user_id,
    followUpFilter !== "all" && followUpFilter,
    activityFilter !== "all" && activityFilter,
    sortBy !== "Recently Added" && sortBy,
    searchQuery,
    selectedLeadSource,
    showOnlyTransferred,
    searchInput,
    !(selectedEndDate.diff(selectedStartDate, "days") >= 28 && selectedEndDate.diff(selectedStartDate, "days") <= 31)
  ].filter(Boolean).length, [formDataFormChoose.user_id, followUpFilter, activityFilter, sortBy, searchQuery, searchInput, selectedLeadSource, showOnlyTransferred, selectedStartDate, selectedEndDate]);

  const handleActivityFilterChange = (option: any) => {
    setActivityFilter(option.value);
  };

  const handleActivityDateChange = (start: moment.Moment, end: moment.Moment) => {
    setActivityStartDate(start);
    setActivityEndDate(end);
  };

  const handleResetFilters = () => {
    handleClearLeadSource();
  };

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;
    if (isDragProcessing) return;

    if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) return;

    const sourcePipeline = source.droppableId;
    const destPipeline = destination.droppableId;

    setIsDragProcessing(true);

    const destStageConfig = pipelinesConfig.find(
      (c: any) => c.stage_name?.toLowerCase().trim() === destPipeline.toLowerCase().trim()
    ) || {};
    const shouldShowReminder = destStageConfig.require_reminder !== false;
    const isWonLostDest =
      destStageConfig.stage_type === "won" || destStageConfig.stage_type === "lost";

    setLeads((prev: any) => {
      const newState = { ...prev };
      const sourceArr = [...(newState[sourcePipeline] || [])];
      const destArr = [...(newState[destPipeline] || [])];

      const movedIndex = sourceArr.findIndex((l) => String(l.key || l.id) === draggableId);
      if (movedIndex === -1) return prev;

      const [moved] = sourceArr.splice(movedIndex, 1);
      destArr.splice(destination.index, 0, { ...moved, pipeline_char: destPipeline });

      newState[sourcePipeline] = sourceArr;
      newState[destPipeline] = destArr;
      return newState;
    });

    setReminderLeads((prev: any) => {
      if (!prev) return prev;
      const apply = (arr: any[]) =>
        isWonLostDest
          ? (arr || []).filter((l: any) => String(l.key || l.id) !== draggableId)
          : (arr || []).map((l: any) =>
              String(l.key || l.id) === draggableId
                ? { ...l, pipeline_char: destPipeline }
                : l,
            );
      return {
        today: apply(prev.today),
        overdue: apply(prev.overdue),
        upcoming: apply(prev.upcoming),
      };
    });

    if (isWonLostDest) {
      setReminderLeadIds((prev: any) => {
        if (!prev) return prev;
        const id = Number(draggableId);
        const drop = (arr: number[]) => (arr || []).filter((k) => k !== id);
        return {
          today: drop(prev.today),
          overdue: drop(prev.overdue),
          upcoming: drop(prev.upcoming),
        };
      });
    }

    try {
      await api.post("leadService/updateLeadResponsePipeline", {
        signature,
        pipeline: destPipeline,
        lead_id: draggableId,
      });

      if (!shouldShowReminder) {
        fetchLeads();
        setIsDragProcessing(false);
      } else {
        setDraggedLeadId(draggableId);
        setShowReminderModal(true);
      }
    } catch (error) {
      showBasicAlert("Failed to update lead");
      fetchLeads();
      setIsDragProcessing(false);
    }
  };

  const handleReminderSubmit = async () => {
    if (selectedReminderDate && draggedLeadId) {
      try {
        const lead: any = Object.values(leads).flat().find((l: any) => String(l.key) === draggedLeadId);

        await api.post("leadService/addLeadFollowUp", {
          signature,
          lead_id: draggedLeadId,
          company_id: lead?.company_id || formDataCompanyChoose.companyId,
          company_type: lead?.company_type || formDataCompanyChoose.companyType,
          follow_up_date: selectedReminderDate,
        });

        showBasicAlert("Follow-up reminder added successfully");
      } catch (e: any) {
        showBasicAlert(e.data?.message?.[0]?.msg || "Failed to add reminder");
      }
    }

    setShowReminderModal(false);
    setSelectedReminderDate(null);
    setDraggedLeadId(null);
    setIsDragProcessing(false);
    fetchLeads();
  };

  const handleReminderSkip = () => {
    setShowReminderModal(false);
    setSelectedReminderDate(null);
    setDraggedLeadId(null);
    setIsDragProcessing(false);
    fetchLeads();
  };


  const handleAcknowledgeTransfer = async () => {
    try {
      setAckLoading(true);
      await api.post("leadService/markLeadTransferStatus", {
        signature,
        company_id: formDataCompanyChoose.companyId,
        company_type: formDataCompanyChoose.companyType,
        form_id: formDataFormChoose.form_id,
      });
      setIsTransferOpen(false);
    } catch (error) {
      console.error(error);
      showBasicAlert("Failed to acknowledge transfers");
    } finally {
      setAckLoading(false);
    }
  };

  const handlePipelineScroll = useCallback((pipeline: string, e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
      const total = filteredLeads[pipeline]?.length || 0;
      setVisibleCountByPipeline((prev) => {
        const current = prev[pipeline] || 30;
        if (current >= total) return prev;
        return { ...prev, [pipeline]: Math.min(current + 20, total) };
      });
    }
  }, []);

  const refreshPage = (type: string) => {
    if (type === "updateUI") {
      forceRefreshLeads();
    }
  };

  // Add this function after your other filter functions
  const filterByFollowUp = useCallback((lead: any) => {
    if (followUpFilter === "all") return true;

    const followUpDate = lead.follow_up_date;

    // Handle "none" filter - leads without follow-up date
    if (!followUpDate) {
      return followUpFilter === "none";
    }

    if (followUpFilter === "none") {
      return false;
    }

    // Use backend-aware reminder tracking to check call status if available
    if (reminderLeadIds && ["today", "overdue", "upcoming"].includes(followUpFilter)) {
      const leadId = Number(lead.key || lead.id);
      if (followUpFilter === "today") return reminderLeadIds.today.includes(leadId);
      if (followUpFilter === "overdue") return reminderLeadIds.overdue.includes(leadId);
      if (followUpFilter === "upcoming") return reminderLeadIds.upcoming.includes(leadId);
    }

    // Fallback: Get date in YYYY-MM-DD format for comparison // Get date in YYYY-MM-DD format for comparison
    const leadMoment = moment(followUpDate);
    const leadLocal = leadMoment.local();
    const leadDateStr = leadLocal.format("YYYY-MM-DD");

    switch (followUpFilter) {
      case "today":
        const todayStr = moment().format("YYYY-MM-DD");
        return leadDateStr === todayStr;

      case "upcoming":
        return leadLocal.isAfter(moment().endOf('day'));

      case "overdue":
        return leadLocal.isBefore(moment().startOf('day'));

      // case "custom":
      //   if (!customFollowUpDate) {
      //     return true;
      //   }
      //   const customDateStr = customFollowUpDate.format("YYYY-MM-DD");
      //   return leadDateStr === customDateStr;
      case "custom":
        if (!reminderCustomStart || !reminderCustomEnd) return true;
        const leadMomentCheck = moment(followUpDate).local();
        return leadMomentCheck.isSameOrAfter(reminderCustomStart, "day") &&
          leadMomentCheck.isSameOrBefore(reminderCustomEnd, "day");

      default:
        return true;
    }
  }, [followUpFilter, customFollowUpDate, reminderLeadIds, reminderCustomStart, reminderCustomEnd]);

  const filteredLeads = useMemo(() => {

    if (!leads || Object.keys(leads).length === 0) return {};

    const result: any = {};

    const role = user?.userDetails?.role_char?.toLowerCase();
    const isAdmin = role === "admin" || role === "employeeadmin";
    const isReminderFilterActive = ["today", "overdue", "upcoming", "custom"].includes(followUpFilter);

    const pipelineKeys =
      isReminderFilterActive && reminderLeads
        ? Array.from(new Set([...Object.keys(leads), ...pipelines]))
        : Object.keys(leads);

    pipelineKeys.forEach((pipeline) => {
      let list = leads[pipeline] || [];

      if (isReminderFilterActive && reminderLeads) {
        let reminderBase: any[] = [];
        if (followUpFilter === "today") {
          reminderBase = reminderLeads.today;
        } else if (followUpFilter === "overdue") {
          reminderBase = reminderLeads.overdue;
        } else if (followUpFilter === "upcoming") {
          reminderBase = reminderLeads.upcoming;
        } else if (followUpFilter === "custom") {
          const allReminders = [...(reminderLeads.today || []), ...(reminderLeads.overdue || []), ...(reminderLeads.upcoming || [])];
          if (!reminderCustomStart || !reminderCustomEnd) {
            reminderBase = allReminders;
          } else {
            reminderBase = allReminders.filter((lead: any) => {
              const followUpDate = lead.follow_up_date;
              if (!followUpDate) return false;
              const leadMomentCheck = moment(followUpDate).local();
              return leadMomentCheck.isSameOrAfter(reminderCustomStart, "day") &&
                leadMomentCheck.isSameOrBefore(reminderCustomEnd, "day");
            });
          }
          const uniqueReminders = new Map(reminderBase.map((item: any) => [item.key || item.id, item]));
          reminderBase = Array.from(uniqueReminders.values());
        }

        if (formDataFormChoose.user_id && formDataFormChoose.user_id !== "0") {
          reminderBase = reminderBase.filter(
            (lead: any) => String(lead.assigned_to) === String(formDataFormChoose.user_id),
          );
        }

        list = reminderBase.filter((lead: any) => lead.pipeline_char?.toLowerCase() === pipeline.toLowerCase());
      } else {
        list = list.filter(filterByFollowUp);
      }

      if (activityLeadIds !== null) {
        list = list.filter((lead: any) => activityLeadIds.includes(Number(lead.key || lead.id)));
      }

      if (showOnlyTransferred) {
        list = list.filter((lead: any) =>
          transferLeadsId.includes(lead.lead_id)
        );
      }

      if (searchQuery) {
        list = list.filter((lead: any) =>
          lead.response?.["Full Name"]?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          lead.mobile_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          lead?.email?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      if (sortBy === "Recently Added") {
        list.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      } else if (sortBy === "Ascending(Name)") {
        list.sort((a: any, b: any) => (a.LeadName || "").localeCompare(b.LeadName || ""));
      } else if (sortBy === "Descending(Name)") {
        list.sort((a: any, b: any) => (b.LeadName || "").localeCompare(a.LeadName || ""));
      } else if (sortBy === "Last Month") {
        const lastMonth = new Date();
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        list = list.filter((lead: any) => new Date(lead.createdAt) >= lastMonth);
      } else if (sortBy === "Last 7 Days") {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        list = list.filter((lead: any) => new Date(lead.createdAt) >= sevenDaysAgo);
      }

      result[pipeline] = list;
    });

    return result;
  }, [leads, pipelines, searchQuery, sortBy, filterByFollowUp, showOnlyTransferred, transferLeadsId, activityLeadIds, followUpFilter, reminderLeads, reminderCustomStart, reminderCustomEnd, formDataFormChoose.user_id]);

  const companyChoose = useMemo(
    () => getAllCompanyLists.map((c: any) => ({ value: c.id, label: `${c.name} - ${c.type}` })),
    [getAllCompanyLists]
  );

  const leadFormChoose = useMemo(
    () => data.map((f: any) => ({ value: `${f.pipeline_id}-${f.key}`, label: f.LeadFormName })),
    [data]
  );

  const userChoose = useMemo(() => {
    const selectedForm = data.find(f => f.key === formDataFormChoose.form_id);

    if (!selectedForm?.employees || selectedForm.employees.length === 0) {
      return [{ value: "0", label: "All Employees" }, ...users.map((u: any) => ({ value: u.key, label: u.Name }))];
    }

    const filteredUsers = users.filter((user: any) =>
      selectedForm.employees.includes(user.key)
    );

    return [
      { value: "0", label: "All Employees" },
      ...filteredUsers.map((u: any) => ({ value: u.key, label: u.Name }))
    ];
  }, [users, data, formDataFormChoose.form_id]);

  const getRandomColor = () => {
    const letters = "0123456789ABCDEF";
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-1">
            <div className="my-auto mb-2">
              <h2 className="mb-1 d-flex align-items-center gap-2">
                👤 Leads
              </h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}><i className="ti ti-smart-home" /></Link>
                  </li>
                  <li className="breadcrumb-item">CRM</li>
                  <li className="breadcrumb-item active">Leads Grid</li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="me-2 mb-2">
                <div className="d-flex align-items-center border bg-white rounded p-1  icon-list">
                  <Link
                    to={`${routes.leadsList}${location?.search}`}
                    state={{ companyDetails: formDataCompanyChoose, formDetails: formDataFormChoose }}
                    className="btn btn-icon btn-sm me-1"
                    title="List">
                    <i className="ti ti-list-tree" />
                  </Link>
                  <Link to={`${routes.leadsGrid}${location?.search}`} className="btn btn-icon btn-sm active bg-primary text-white" title="Grid">
                    <i className="ti ti-layout-grid" />
                  </Link>
                </div>
              </div>
              <div className="dropdown me-2 mb-2" style={{ maxWidth: "190px" }}>
                <CommonSelect
                  className="select"
                  options={leadFormChoose}
                  defaultValue={{ value: `${formDataFormChoose.pipeline_id}-${formDataFormChoose.form_id}`, label: formDataFormChoose.form_name }}
                  onChange={handleLeadFormChange}
                />
              </div>
              <div className="dropdown me-2 mb-2" style={{ maxWidth: "210px" }}>
                <CommonSelect
                  className="select"
                  options={companyChoose}
                  defaultValue={{ value: formDataCompanyChoose.companyId, label: `${formDataCompanyChoose.companyName}-${formDataCompanyChoose.companyType}` }}
                  onChange={handleCompanyChange}
                />
              </div>
              <div className="dropdown me-2 mb-2" style={{ position: "relative", maxWidth: "150px" }}>
                <ReminderDropdown
                  followUpFilter={followUpFilter}
                  setFollowUpFilter={setFollowUpFilter}
                  reminderLeadIds={reminderLeadIds}
                  isReminderLoading={isReminderLoading}
                  reminderCustomStart={reminderCustomStart}
                  reminderCustomEnd={reminderCustomEnd}
                  setReminderCustomStart={setReminderCustomStart}
                  setReminderCustomEnd={setReminderCustomEnd}
                  onOpen={() => setReminderDropdownOpened(true)}
                />
              </div>
              <div className="me-2 mb-2">
                {!formDataFormChoose.form_id ? (
                  <button className="btn btn-primary d-flex align-items-center" disabled>
                    <i className="ti ti-circle-plus me-1" />Add Lead
                  </button>
                ) : (
                  <Link to="#" data-bs-toggle="modal" data-bs-target="#add_leads" className="btn btn-primary d-flex align-items-center">
                    <i className="ti ti-circle-plus me-1" />Add Lead
                  </Link>
                )}
              </div>
              <div className="mb-2 d-flex align-items-center gap-1">
                <button
                  onClick={() => setShowFilters((v) => !v)}
                  style={{
                    height: "38px",
                    padding: "0 18px",
                    borderRadius: "999px",
                    border: "1.5px solid #dbeafe",
                    background: "#e8f1fc",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    cursor: "pointer",
                    fontWeight: 500,
                    fontSize: "14px",
                    color: "#232360",
                    whiteSpace: "nowrap",
                    transition: "background 0.2s ease"
                  }}>
                  <SlidersHorizontal size={16} />
                  {showFilters ? "Hide Filters" : "Show Filters"}
                  {activeFilterCount > 0 && (
                    <span className="badge bg-primary rounded-circle ms-1" style={{ fontSize: "10px", padding: "4px 6px" }}>
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                {activeFilterCount > 0 && (
                  <button
                    onClick={handleResetFilters}
                    className="btn btn-outline-danger d-flex align-items-center gap-1"
                    style={{
                      height: "38px",
                      padding: "0 15px",
                      borderRadius: "999px",
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                  >
                    <i className="ti ti-trash fs-16" />
                    Clear All
                  </button>
                )}
              </div>
              <div className="head-icons ms-2"><CollapseHeader /></div>
            </div>
          </div>

          <div className="mb-3">
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                background: "#f1f3ff",
                border: "1px solid #dbe4ff",
                borderLeft: "4px solid #3b5bdb",
                padding: "10px 14px",
                borderRadius: "8px",
                fontSize: "13px",
                color: "#2b2f36",
                lineHeight: 1.5,
              }}
            >
              <i
                className="ti ti-info-circle"
                style={{
                  fontSize: "16px",
                  color: "#3b5bdb",
                  marginTop: "2px",
                }}
              />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ color: "#495057" }}>
                  Showing data from{" "}
                  <span style={{ fontWeight: 500, color: "#3b5bdb" }}>
                    {selectedStartDate.format("DD MMM YYYY")}
                  </span>{" "}
                  to{" "}
                  <span style={{ fontWeight: 500, color: "#3b5bdb" }}>
                    {selectedEndDate.format("DD MMM YYYY")}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {isPageLoading || isLoadingData ? (<div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading leads data...</span>
            </div>
          </div>) : (<>
            <div className="livecall-layout align-top">
              <div className="livecall-table-section">

                {pipelines.length > 0 && data.length > 0 && (
                  <div className="row">
                    {HeaderGrid.map((stat: any, index: number) => (
                      <div className="col-xl-3 col-md-6 mb-1" key={index}>
                        <div className="card position-relative border-0 shadow-sm h-60" style={{ borderRadius: "12px", overflow: "hidden" }}>
                          <div className="card-body position-relative z-1 bg-white">
                            <div className="d-flex align-items-center mb-3">
                              <div className={`avatar avatar-md rounded-circle d-flex align-items-center justify-content-center ${stat.color}`} style={{ width: "40px", height: "40px", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" }}>
                                <i className={`ti ${stat.icon} text-white fs-20`} />
                              </div>
                              <div className="ms-3">
                                <p className="fw-semibold text-muted mb-1 text-truncate">{stat.title}</p>
                                <h5 className="mb-0 fw-bold text-dark">{stat.value}</h5>
                              </div>
                            </div>
                            <span className="position-absolute top-0 end-0 opacity-10" style={{ zIndex: 0 }}>
                              <ImageWithBasePath src="assets/img/bg/card-bg-04.png" alt="bg" />
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {data.length === 0 && (
                  <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "300px" }}>
                    <div className="text-center">
                      <div style={{ fontSize: "40px", color: "#ccc" }}>📭</div>
                      <p className="mt-2 text-muted fs-50">No Leads</p>
                    </div>
                  </div>
                )}

                {pipelines.length > 0 && data.length > 0 && filteredLeads && Object.keys(filteredLeads).length > 0 && (
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <div className="d-flex overflow-x-auto align-items-start mb-2">
                      {pipelines.map((pipeline, index) => {
                        const list = filteredLeads[pipeline] || [];
                        const visibleCount = visibleCountByPipeline[pipeline] || Math.min(list.length, 30);
                        const visibleLeads = list.slice(0, visibleCount);

                        return (
                          <div key={pipeline} className="kanban-list-items bg-white">
                            <div className="card mb-1 border-0 shadow-sm" style={{ borderRadius: "12px" }}>
                              <div className="card-body d-flex justify-content-between  py-3 px-4" style={{ backgroundColor: pipelinesColor?.[index] || getRandomColor() }}>
                                <div className="">
                                  <h5 className="fw-semibold mb-1 d-flex align-items-center text-white">
                                    <span className="d-inline-flex align-items-center justify-content-center rounded-circle me-2" style={{ width: "12px", height: "12px", backgroundColor: "#FFFFFF" }} />
                                    {pipeline}
                                  </h5>
                                  <span className="fw-medium text-white small" title={!(selectedEndDate.diff(selectedStartDate, "days") >= 28 && selectedEndDate.diff(selectedStartDate, "days") <= 31) ? `${selectedStartDate.format("DD MMM YYYY")} - ${selectedEndDate.format("DD MMM YYYY")}` : ""}>
                                    {list.length || 0}{" "}
                                    {selectedEndDate.diff(selectedStartDate, "days") >= 28 && selectedEndDate.diff(selectedStartDate, "days") <= 31
                                      ? "Monthly"
                                      : ""}{" "}
                                    Leads
                                  </span>
                                </div>

                                <div className="h-full ">
                                  <span className="fw-medium text-white large">     {overallStageCounts[pipeline] ?? 0} Total </span>
                                </div>
                              </div>

                            </div>

                            <Droppable droppableId={pipeline}>
                              {(provided) => (
                                <div
                                  className="kanban-drag-wrap pt-4"
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  style={{ maxHeight: "70vh", overflowY: "auto" }}
                                  onScroll={(e) => handlePipelineScroll(pipeline, e)}>
                                  {visibleLeads.map((lead: any, idx: number) => (
                                    <Draggable key={lead.key || lead.id} draggableId={String(lead.key || lead.id)} index={idx}
                                      isDragDisabled={isDragProcessing}
                                    >
                                      {(providedDrag) => (
                                        <div
                                          ref={providedDrag.innerRef}
                                          {...providedDrag.draggableProps}
                                          {...providedDrag.dragHandleProps}
                                          className="card kanban-card shadow-sm border-0"
                                          style={{ borderLeft: "4px solid #4caf50", backgroundColor: "#f9f9fc", marginBottom: "8px", ...providedDrag.draggableProps.style }}>
                                          <div className="card-body p-3 pb-2">
                                            <div className="d-flex align-items-center mb-2">
                                              <Link
                                                to={`${routes.leadsDetails}${location?.search}`}
                                                state={{
                                                  lead, data, pipelines, pipelinesConfig, type: "lead_grid", selectedCompanyDetails: formDataCompanyChoose, selectedLeadFormDetails: formDataFormChoose,
                                                  preserveFilters: {
                                                    companyId: formDataCompanyChoose.companyId,
                                                    companyName: formDataCompanyChoose.companyName,
                                                    companyType: formDataCompanyChoose.companyType,
                                                    pipeline_id: formDataFormChoose.pipeline_id,
                                                    form_name: formDataFormChoose.form_name,
                                                    form_id: formDataFormChoose.form_id,
                                                    user_id: formDataFormChoose.user_id,
                                                    user_name: formDataFormChoose.user_name,
                                                    selectedLeadSource: selectedLeadSource,
                                                    selectedStartDate: selectedStartDate.format('YYYY-MM-DD'),
                                                    selectedEndDate: selectedEndDate.format('YYYY-MM-DD'),
                                                    sortBy: sortBy,
                                                    searchQuery: searchQuery,
                                                    searchInput: searchInput,
                                                    followUpFilter: followUpFilter,
                                                    customFollowUpDate: customFollowUpDate ? customFollowUpDate.format('YYYY-MM-DD') : null,
                                                    showOnlyTransferred: showOnlyTransferred,
                                                    activityFilter: activityFilter,
                                                    activityStartDate: activityStartDate.format('YYYY-MM-DD'),
                                                    activityEndDate: activityEndDate.format('YYYY-MM-DD'),
                                                    tempSearchQuery: tempSearchQuery,
                                                    showFilters: showFilters
                                                  }
                                                }}
                                                className="avatar avatar-sm bg-primary text-white fw-semibold rounded-circle d-flex align-items-center justify-content-center me-2 text-uppercase shadow-sm"
                                                style={{ width: 38, height: 38, fontSize: "13px" }}>
                                                {lead.response["Full Name"]?.split(" ").map((word: any) => word[0]).join("").toUpperCase()}
                                              </Link>
                                              <div className="flex-grow-1">
                                                <h6 className="mb-0 fw-semibold text-truncate" style={{ fontSize: "14px" }}>
                                                  <Link
                                                    to={`${routes.leadsDetails}${location?.search}`}
                                                    state={{
                                                      lead,
                                                      data,
                                                      pipelines,
                                                      pipelinesConfig,
                                                      type: "lead_grid",
                                                      selectedCompanyDetails: formDataCompanyChoose,
                                                      selectedLeadFormDetails: formDataFormChoose,
                                                      preserveFilters: {
                                                        companyId: formDataCompanyChoose.companyId,
                                                        companyName: formDataCompanyChoose.companyName,
                                                        companyType: formDataCompanyChoose.companyType,
                                                        pipeline_id: formDataFormChoose.pipeline_id,
                                                        form_name: formDataFormChoose.form_name,
                                                        form_id: formDataFormChoose.form_id,
                                                        user_id: formDataFormChoose.user_id,
                                                        user_name: formDataFormChoose.user_name,
                                                        selectedLeadSource: selectedLeadSource,
                                                        selectedStartDate: selectedStartDate.format('YYYY-MM-DD'),
                                                        selectedEndDate: selectedEndDate.format('YYYY-MM-DD'),
                                                        sortBy: sortBy,
                                                        searchQuery: searchQuery,
                                                        searchInput: searchInput,
                                                        followUpFilter: followUpFilter,
                                                        customFollowUpDate: customFollowUpDate ? customFollowUpDate.format('YYYY-MM-DD') : null,
                                                        showOnlyTransferred: showOnlyTransferred,
                                                        activityFilter: activityFilter,
                                                        activityStartDate: activityStartDate.format('YYYY-MM-DD'),
                                                        activityEndDate: activityEndDate.format('YYYY-MM-DD'),
                                                        tempSearchQuery: tempSearchQuery,
                                                        showFilters: showFilters
                                                      }
                                                    }}
                                                    className="text-dark text-decoration-none">
                                                    {lead.response["Full Name"]}
                                                  </Link>
                                                </h6>
                                                <small className="text-muted">{lead.company_name}</small>
                                              </div>
                                              <div className="ms-auto">
                                                <IconButton
                                                  size="small"
                                                  onClick={(event) => {
                                                    setAnchorEl(event.currentTarget);
                                                    setSelectedLead({
                                                      structure: data.find((item) => parseInt(item.key) === parseInt(lead.form_id))?.structure,
                                                      responses: lead.response,
                                                      id: lead?.key,
                                                    });
                                                  }}>
                                                  <MoreVertIcon fontSize="small" />
                                                </IconButton>
                                              </div>
                                            </div>

                                            <div className="mb-2 small text-muted">
                                              <div className="d-flex align-items-center mb-1">
                                                <i className="ti ti-circle text-primary me-2" />
                                                <span>{lead.lead_id || "N/A"}</span>
                                              </div>
                                              <div className="d-flex align-items-center mb-1">
                                                <i className="ti ti-mail text-info me-2" />
                                                <span>{lead.email || "N/A"}</span>
                                              </div>
                                              <div className="d-flex align-items-center mb-1">
                                                <i className="ti ti-phone text-success me-2" />
                                                <span>{lead.mobile_no || "N/A"}</span>
                                              </div>
                                              <div className="d-flex align-items-center">
                                                <i className="ti ti-clock text-warning me-2" />
                                                <span>{humanReadableDate(lead.createdAt, true, undefined, undefined, false)}</span>
                                              </div>
                                            </div>

                                            <div className="d-flex align-items-center justify-content-between border-top pt-2 mt-2">
                                              <div className="d-flex align-items-center">
                                                <span className="avatar avatar-xs me-2">
                                                  <ImageWithBasePath src={lead.Logo || "assets/img/company/company-01.svg"} alt="image" className="img-fluid rounded-circle" />
                                                </span>
                                                <small className="text-dark text-truncate" style={{ maxWidth: "100px" }}>
                                                  {lead?.assigned_to_name?.trim() || "N/A"}
                                                </small>
                                              </div>
                                              <div className="d-flex align-items-center gap-2">
                                                <button
                                                  onClick={() => handleCall(lead)}
                                                  disabled={!!call.activeCall}
                                                  title={call.activeCall ? "End current call before starting auto call" : ""}
                                                  style={{
                                                    background: "none",
                                                    border: "none",
                                                    padding: 0,
                                                    cursor: call.activeCall ? "not-allowed" : "pointer",
                                                    opacity: call.activeCall ? 0.4 : 1,
                                                    lineHeight: 1,
                                                  }}
                                                >
                                                  <i className="ti ti-phone-check text-secondary" />
                                                </button>

                                                <Link to="#" onClick={(e) => { e.preventDefault(); showBasicAlert("Coming Soon"); }}>
                                                  <i className="ti ti-message-circle-2 text-secondary" />
                                                </Link>
                                                <Link
                                                  to="#"
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    const phone = lead.mobile_no?.replace(/\D/g, "");
                                                    phone ? window.open(`https://wa.me/${phone}`, "_blank") : showBasicAlert("Phone number not available");
                                                  }}>
                                                  <i className="ti ti-brand-whatsapp text-success" />
                                                </Link>
                                                <small className="text-muted">{moment(lead.createdAt.split(",")[0]).format("MMM D")}</small>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </Draggable>
                                  ))}
                                  {provided.placeholder}
                                  {list.length > visibleLeads.length && (
                                    <div className="text-center my-2">
                                      <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() =>
                                          setVisibleCountByPipeline((prev) => ({
                                            ...prev,
                                            [pipeline]: Math.min((prev[pipeline] || 30) + 20, list.length),
                                          }))
                                        }>
                                        Load more ({list.length - visibleLeads.length} more)
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </Droppable>
                          </div>
                        );
                      })}
                    </div>
                  </DragDropContext>
                )}
              </div>

              {/* Filter Sidebar */}
              <div className={`slide-in-left livecall-filters-section${showFilters ? "" : " hidden"}`}>
                <LeadsGridFilters
                  showFilters={showFilters}
                  userRole={user?.userDetails.role_char.toLowerCase()}
                  userChoose={userChoose}
                  followUpOptions={followUpOptions}
                  activityFilterOptions={activityFilterOptions}
                  formDataFormChoose={formDataFormChoose}
                  followUpFilter={followUpFilter}
                  customFollowUpDate={customFollowUpDate}
                  activityFilter={activityFilter}
                  activityStartDate={activityStartDate}
                  activityEndDate={activityEndDate}
                  sortBy={sortBy}
                  selectedStartDate={selectedStartDate}
                  selectedEndDate={selectedEndDate}
                  searchQuery={searchQuery}
                  tempSearchQuery={tempSearchQuery}
                  isActivityLoading={isActivityLoading}
                  activeFilterCount={activeFilterCount}
                  showOnlyTransferred={showOnlyTransferred}
                  selectedLeadSource={selectedLeadSource}
                  leadSource={leadSource}
                  setShowOnlyTransferred={setShowOnlyTransferred}
                  handleLeadSourceChange={handleLeadSourceChange}
                  handleUserChange={handleUserChange}
                  setFollowUpFilter={setFollowUpFilter}
                  setCustomFollowUpDate={setCustomFollowUpDate}
                  handleActivityFilterChange={handleActivityFilterChange}
                  handleActivityDateChange={handleActivityDateChange}
                  setSortBy={setSortBy}
                  handleDateChange={handleDateChange}
                  setTempSearchQuery={setTempSearchQuery}
                  handleSearchApply={handleSearchApply}
                  handleResetFilters={handleResetFilters}
                />
              </div>

            </div>
          </>)}

        </div>

        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2026 © CMS.</p>
          <p>
            Designed & Developed By <Link to="#" onClick={(e) => e.preventDefault()} className="text-primary">ZYVO</Link>
          </p>
        </div>
      </div>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => { setIsEditOpen(true); setAnchorEl(null); }}>Edit Lead</MenuItem>
      </Menu>

      <AddLeads
        companyList={getAllCompanyListsForAdd}
        formsList={data}
        users={users}
        lastLeadData={Object.values(leads).flat()}
        refreshPage={refreshPage}
      />

      {selectedLead && (
        <EditLead
          open={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          updateUI={() => {
            setIsEditOpen(false);
            forceRefreshLeads();
          }}
          data={[selectedLead]}
        />
      )}

      <Dialog open={isTransferOpen} onClose={(_, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; }} disableEscapeKeyDown fullWidth maxWidth="md">
        <DialogTitle>
          New Lead Transfers
          <Typography variant="body2" color="text.secondary">The following leads were transferred to you.</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {!newTransferLead || newTransferLead.length === 0 ? (
            <Typography>No transfers found.</Typography>
          ) : (
            <Grid container spacing={2}>
              {newTransferLead.map((item: any) => (
                <Grid item xs={12} key={item.key}>
                  <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #eee", bgcolor: "#fafafa" }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2">Lead ID: <b>{item.lead_id || "-"}</b></Typography>
                        <Typography variant="body2" color="text.secondary">Stage: {item.Stage || "-"}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Transfer Date: {item.TransferDate ? moment(item.TransferDate).format("DD MMM YYYY, HH:mm") : "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" fontWeight={600} gutterBottom>Lead</Typography>
                        <Typography variant="body2">Name: {item.Name || "-"}</Typography>
                        <Typography variant="body2" color="text.secondary">Mobile: {item.MobileNo || "-"}</Typography>
                      </Grid>
                      <Grid item xs={12}><Divider /></Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" fontWeight={600} gutterBottom>Transfer</Typography>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar src={item.FromImage || ""} sx={{ width: 30, height: 30 }} />
                            <Typography variant="body2">{item.From || "-"}</Typography>
                          </Box>
                          <Typography variant="body2">→</Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar src={item.ToImage || ""} sx={{ width: 30, height: 30 }} />
                            <Typography variant="body2">{item.To || "-"}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="contained" onClick={handleAcknowledgeTransfer} disabled={ackLoading}>
            {ackLoading ? "Please wait..." : "Okay"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showReminderModal}
        // onClose={(_, reason) => {
        //   if (reason === "backdropClick") return;
        //   handleReminderSkip();
        // }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add Follow-Up Reminder
          <Typography variant="body2" color="text.secondary">
            Set a reminder for this lead after moving to new stage
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DatePicker
            className="form-control datetimepicker w-100 mt-3"
            format="DD-MM-YYYY HH:mm"
            showTime={{ format: "HH:mm" }}
            onChange={(date: any) => setSelectedReminderDate(date)}
            placeholder="Select reminder date and time"
            disabledDate={(current: any) =>
              !!current &&
              current.valueOf() < new Date().setHours(0, 0, 0, 0)
            }
          />
        </DialogContent>
        <DialogActions>
          {/* <Button onClick={handleReminderSkip} color="inherit">
            Skip
          </Button> */}
          <Button onClick={handleReminderSubmit} variant="contained" disabled={!selectedReminderDate}>
            Save Reminder
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LeadsGrid;
